/**
 * The tracker feed (phase 2b.4; docs/specs/tracker.md §5): polls the tenant's fleet tracker accounts through the
 * provider proxy at the Diurnal intervals, parses each answer with its adapter, keeps trails for up to 48 hours (Q48),
 * attributes positions to the tenant's tracker assets, keeps one icon per aircraft, marks RFDS aircraft (Q46), and
 * hands the map an `AircraftFeed` (docs/specs/map.md §8). Offline, the last positions stay with their fetch time;
 * nothing fails silently: every account has a status in words.
 *
 * History (§5.1; Diurnal parity 03-003, 03-006, 03-009): after an account's first good poll, and after the history
 * depth changes, the trails fill in from the provider's own history as the Diurnal tool does: V2Track trips of the
 * depth's days (GetHistoryNew, then GetTripDataBulk in batches of 20) and the current trip of each active aircraft
 * every minute; Spider Tracks completed flights inside the depth (the list every 2 minutes, each flight once);
 * WebSentinel each vehicle's position CSV. Never more than the 48 hours kept (decision 75).
 *
 * No timers or network of its own: the app passes the proxy client and calls `poll()` on its own timer.
 */
import type { AircraftFeed, AircraftPosition, ObservedFeedInput, Unsubscribe } from './aircraft-layer.js';
import { observedCallsign, observedPoint, type ObservableSource } from './observed-traffic.js';
import {
  parseSpiderTrack,
  parseSpiderTracks,
  parseSpiderTracksList,
  parseTracPlusKml,
  parseV2TrackAlerts,
  parseV2TrackAssets,
  parseV2TrackHistory,
  parseV2TrackPositions,
  parseV2TrackTrips,
  parseWebSentinel,
  parseWebSentinelCsv,
  v2trackHistoryRange,
  type TrackWithTrail,
  type V2TrackAsset,
} from './tracker-adapters.js';
import { flightStatus } from './flight-status.js';
import { tracPlusFeedHours } from './provider-protocols.js';
import { withNafc, type NafcIndex } from './nafc.js';
import {
  PUBLIC_TRAFFIC_SOURCES,
  createPublicTraffic,
  publicTrailPoint,
  type PublicTrafficFeed,
  type OpenSkyTokenSource,
  type PublicTrafficFetcher,
  type PublicTrafficPrefs,
} from './public-traffic.js';
import {
  DEFAULT_TRACKING_SETTINGS,
  HISTORY_DEPTH_HOURS,
  PUBLIC_TRAFFIC,
  TRAIL_RETENTION_MS,
  alertsToQueue,
  assetFor,
  trackingAlerts,
  TRACKER_SOURCE_NAME,
  isPublicTraffic,
  isRfds,
  kindFromTypeText,
  lostTrackingMinutes,
  mergeTrail,
  onePerAircraft,
  rfdsObservation,
  toAircraftPosition,
  withStatus,
  type FleetProviderId,
  type LandingStrip,
  type PublicTrafficId,
  type QueuedTrackingAlert,
  type RfdsIdentity,
  type TrackPoint,
  type TrackedPosition,
  type TrackerAsset,
  type TrackingSettings,
} from './tracker.js';

/** An answer of the provider proxy for a json or text operation. */
export interface ProxyAnswer {
  readonly body: string;
  readonly fetchedAt: string;
  readonly cached: boolean;
}

/** A refusal of the proxy: `code` as the Edge Function sends it, `message` its words. */
export class ProxyError extends Error {
  constructor(
    readonly code:
      | 'not_set_up'
      | 'choose_account'
      | 'forbidden'
      | 'rate_limited'
      | 'credential_refused'
      | 'provider_error'
      | 'signed_out'
      | 'offline',
    message: string,
    /** The proxy's HTTP status, when it answered (a TA Live 5xx arrives as 502; decides a retry, sign-off 02-001). */
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ProxyError';
  }
}

export interface ProxyClient {
  call(
    provider: string,
    label: string,
    op: string,
    params?: Readonly<Record<string, unknown>>,
  ): Promise<ProxyAnswer>;
}

export interface TrackerAccount {
  readonly provider: FleetProviderId;
  /** The integration's label ('' for the only account of that provider). */
  readonly label: string;
}

export type AccountState = 'waiting' | 'ok' | 'not_set_up' | 'refused' | 'offline' | 'error';

export interface AccountStatus {
  readonly provider: FleetProviderId;
  readonly label: string;
  readonly state: AccountState;
  /** Words for the status bar: "V2Track: 4 aircraft, updated 10:42". */
  readonly message: string;
  readonly lastOkAt: string | null;
  readonly aircraft: number;
}

/** Poll intervals (am:97–107): V2Track 10 s, Spider Tracks and WebSentinel 30 s, TracPlus 60 s; V2Track SOS 30 s. */
export const POLL_MS: Readonly<Record<FleetProviderId, number>> = {
  v2track: 10_000,
  spidertracks: 30_000,
  websentinel: 30_000,
  tracplus: 60_000,
};
const V2TRACK_ALERTS_MS = 30_000;
const V2TRACK_ASSETS_MS = 10 * 60_000;
/** The Diurnal history depth by default (am:747): 12 hours. */
export const DEFAULT_HISTORY_HOURS = 12;
/** V2Track current trips every 60 s (am:139); the Spider Tracks flight list every 2 minutes (am:157). */
const V2TRACK_TRIPS_MS = 60_000;
const SPIDERTRACKS_HISTORY_MS = 2 * 60_000;
/** A history call that failed is tried again after 2 minutes. */
const HISTORY_RETRY_MS = 2 * 60_000;
/** History calls per pass, inside each provider's calls a minute (V2Track 60, Spider Tracks 30, WebSentinel 20). */
const HISTORY_PER_PASS: Readonly<Record<FleetProviderId, number>> = {
  v2track: 20,
  spidertracks: 8,
  websentinel: 8,
  tracplus: 0,
};
const V2TRACK_BATCH = 20;

/** The nearest history depth choice at or above `hours`, at most the 48 hours kept (decision 75). */
export function historyDepth(hours: number | undefined): number {
  const h = Number.isFinite(hours) ? (hours as number) : DEFAULT_HISTORY_HOURS;
  return HISTORY_DEPTH_HOURS.find((d) => d >= h) ?? HISTORY_DEPTH_HOURS[HISTORY_DEPTH_HOURS.length - 1]!;
}

export interface TrackerFeedOptions {
  readonly client: ProxyClient;
  readonly accounts: readonly TrackerAccount[];
  readonly assets?: readonly TrackerAsset[];
  readonly settings?: TrackingSettings;
  readonly rfds?: readonly RfdsIdentity[];
  /** History depth in hours (1, 2, 4, 6, 12, 24 or 48; 12 by default): the history fetched and the TracPlus feed
   * (at most 24 hours: TracPlus refuses 48). */
  readonly historyHours?: number;
  readonly now: () => number;
  /** A new RFDS identity seen on a feed (the app saves it to `rfds_identity`). */
  readonly onRfds?: (identity: { icao_hex: string; registration: string; callsign: string }) => void;
  /** Trail points kept from an earlier run (Field's tracker_point table), by map id. */
  readonly initialTrails?: ReadonlyMap<string, readonly TrackPoint[]>;
  /** Public air traffic around the map view (Teslyn Field's own requests); none without it. */
  readonly publicTraffic?: PublicTrafficFetcher;
  readonly publicPrefs?: PublicTrafficPrefs;
  /** OpenSky's access token from Teslyn Cloud (the organisation's API client); none: OpenSky is not set up. */
  readonly openSky?: OpenSkyTokenSource | null;
  /** Decision 378: whether this map window is the one being looked at (only it asks the public services). */
  readonly isActiveWindow?: () => boolean;
  /** Decision 405: whether the organisation may use adsb.fi; none: allowed. */
  readonly adsbFi?: () => boolean;
  /** Decision 476: whether the organisation may use bushfire.io (Thomson only); none: not allowed. */
  readonly bushfireIo?: () => boolean;
  /** Decision 404: NAFC's fire call-signs, to label public aircraft; none or null: no labels. */
  readonly nafc?: () => NafcIndex | null;
  /** The aircraft pinned on the project and their observed trails (03-041; `setObserved` changes them). */
  readonly observed?: ObservedFeedInput;
  /** The strips of the project's flight plan (its airport and alternate pins), landing places of the evidence rule
   * (decision 949; `setStrips` changes them). None: the aerodrome list and the flight's own start-up position. */
  readonly strips?: readonly LandingStrip[];
}

export interface TrackerFeed extends AircraftFeed {
  /** Poll every account whose interval has passed (call it every few seconds). */
  poll(): Promise<void>;
  /** Resolves when every history request started so far has been answered and drawn. */
  settled(): Promise<void>;
  status(): readonly AccountStatus[];
  /** Trails per map id (the `AircraftPosition.id`), up to 48 hours. */
  trails(): ReadonlyMap<string, readonly TrackPoint[]>;
  /** The positions behind the map list, for the client view. */
  tracked(): readonly { position: TrackedPosition; asset: TrackerAsset | null; map: AircraftPosition }[];
  /** Every device each account reports (before one icon per aircraft), for adding tracker assets. */
  devices(): readonly TrackedPosition[];
  setAssets(assets: readonly TrackerAsset[]): void;
  setSettings(settings: TrackingSettings): void;
  /** A new history depth: the history is asked for again at the next poll. */
  setHistoryHours(hours: number): void;
  setView(view: { south: number; west: number; north: number; east: number }): void;
  setPublicTraffic(prefs: PublicTrafficPrefs): void;
  publicStatus(): readonly { source: string; message: string; ok: boolean; credit: string }[];
  /**
   * The pinned aircraft (03-041): shown whatever the radius, with every fix in their trail (not only while moving),
   * their trail kept 48 hours like the trackers' with the trail this laptop observed merged in, and shown at their
   * last observed position when no feed lists them (the Diurnal grey marker, am:1521–1609), up to 48 hours.
   */
  setObserved(observed: ObservedFeedInput): void;
  /** The project's flight-plan strips (decision 949): the aircraft are judged again with them. */
  setStrips(strips: readonly LandingStrip[]): void;
  /** Every position of the public sources switched on, wherever it is (auto-pin, 03-042). */
  worldTraffic(): readonly TrackedPosition[];
  /** Lost-tracking, SOS and resumed alerts of the organisation's aircraft raised since the last call (03-037). */
  takeAlerts(): readonly QueuedTrackingAlert[];
}

/** The device id of a pinned aircraft drawn at its last observed position. */
const GHOST = 'observed:';
/** At most this many alerts wait for `takeAlerts` (nobody draining them must not grow memory). */
const ALERT_QUEUE_LIMIT = 200;

/** How long a trail is kept: a public aircraft's as its source keeps it (am:467–468), every other 48 hours. */
function trailCutoff(source: PublicTrafficId | null, nowMs: number): number {
  return source ? nowMs - PUBLIC_TRAFFIC_SOURCES[source].trailHours * 3_600_000 : -Infinity;
}

/** The public source of a trail id with no aircraft on the map ("adsb::7c3ad2"), else null. */
function publicSourceOfId(id: string): PublicTrafficId | null {
  const s = id.slice(0, Math.max(0, id.indexOf(':')));
  return (PUBLIC_TRAFFIC as readonly string[]).includes(s) ? (s as PublicTrafficId) : null;
}

/** History of one device (V2Track GUID, Spider Tracks registration, WebSentinel vehicle id). */
interface HistoryEntry {
  trail: TrackPoint[];
  /** Where the aircraft was at the end of its last completed flight (Spider Tracks shows no landed aircraft). */
  position: TrackedPosition | null;
  /** Points not yet drawn into the aircraft's trail. */
  fresh: boolean;
}

interface AccountRuntime {
  readonly account: TrackerAccount;
  nextAt: number;
  nextAlertsAt: number;
  nextAssetsAt: number;
  sos: Set<string>;
  assets: V2TrackAsset[];
  latest: TrackWithTrail[];
  status: AccountStatus;
  // history
  history: Map<string, HistoryEntry>;
  /** The depth the done set belongs to (null: no history asked yet). */
  historyDepth: number | null;
  /** Devices, vehicles or flights already answered for this depth. */
  done: Set<string>;
  nextHistoryAt: number;
  nextTripsAt: number;
  historyBusy: boolean;
  historyProblem: string | null;
  /** V2Track aircraft types from the trip answers (`td`), by device. */
  typeText: Map<string, string>;
}

const key = (a: TrackerAccount): string => `${a.provider}:${a.label}`;
const accountName = (a: TrackerAccount): string =>
  a.label ? `${TRACKER_SOURCE_NAME[a.provider]} (${a.label})` : TRACKER_SOURCE_NAME[a.provider];
const words = (e: unknown): string => (e instanceof Error ? e.message : String(e));

function clock(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function createTrackerFeed(opts: TrackerFeedOptions): TrackerFeed {
  let assets: readonly TrackerAsset[] = opts.assets ?? [];
  let settings: TrackingSettings = opts.settings ?? DEFAULT_TRACKING_SETTINGS;
  let depth = historyDepth(opts.historyHours);
  const rfds: RfdsIdentity[] = [...(opts.rfds ?? [])];
  // pinned aircraft (03-041) and the alert queue (03-037)
  let pinned = new Map<string, ObservableSource>(
    (opts.observed?.pins ?? []).map((p) => [p.callsign, p.source]),
  );
  let observedTrails: ReadonlyMap<string, readonly TrackPoint[]> = opts.observed?.trails ?? new Map();
  let strips: readonly LandingStrip[] = opts.strips ?? [];
  let alertActive = new Set<string>();
  let alertQueue: QueuedTrackingAlert[] = [];
  const isPinned = (p: TrackedPosition): string | null => {
    const cs = observedCallsign(p);
    return cs !== null && pinned.has(cs) ? cs : null;
  };
  const listeners = new Set<(p: readonly AircraftPosition[]) => void>();
  const trailStore = new Map<string, TrackPoint[]>();
  for (const [id, t] of opts.initialTrails ?? []) {
    const kept = mergeTrail([], t, opts.now());
    if (kept.length > 0) trailStore.set(id, kept);
  }
  const pending = new Set<Promise<void>>();
  let view: { position: TrackedPosition; asset: TrackerAsset | null; map: AircraftPosition }[] = [];
  const traffic: PublicTrafficFeed | null = opts.publicTraffic
    ? createPublicTraffic({
        fetcher: opts.publicTraffic,
        now: opts.now,
        openSky: opts.openSky ?? null,
        ...(opts.isActiveWindow ? { isActiveWindow: opts.isActiveWindow } : {}),
        ...(opts.adsbFi ? { adsbFi: opts.adsbFi } : {}),
        ...(opts.bushfireIo ? { bushfireIo: opts.bushfireIo } : {}),
        ...(opts.publicPrefs ? { prefs: opts.publicPrefs } : {}),
      })
    : null;
  traffic?.setKeep((p) => isPinned(p) !== null);
  const runtimes = new Map<string, AccountRuntime>(
    opts.accounts.map((a) => [
      key(a),
      {
        account: a,
        nextAt: 0,
        nextAlertsAt: 0,
        nextAssetsAt: 0,
        sos: new Set(),
        assets: [],
        latest: [],
        status: {
          provider: a.provider,
          label: a.label,
          state: 'waiting',
          message: `${accountName(a)}: connecting…`,
          lastOkAt: null,
          aircraft: 0,
        },
        history: new Map(),
        historyDepth: null,
        done: new Set(),
        nextHistoryAt: 0,
        nextTripsAt: 0,
        historyBusy: false,
        historyProblem: null,
        typeText: new Map(),
      },
    ]),
  );

  /** History-only aircraft of an account: a completed flight's end, when the provider shows the aircraft no more. */
  function historyOnly(r: AccountRuntime): TrackedPosition[] {
    const live = new Set(r.latest.map((t) => t.position.deviceId));
    return [...r.history]
      .filter(([dev, e]) => e.position !== null && !live.has(dev))
      .map(([, e]) => e.position!);
  }

  /**
   * A position judged by its trail at the fix (decision 860, issue #70; `flyingFromTrail`): the tracker's status
   * words, then the landing evidence, when the provider gave no flying flag. V2Track's words are on the device's
   * history trail (the current trip's points, refreshed every minute, `v2trackCurrentTrips`); TracPlus's "Last
   * Event" words and Spider Tracks' point descriptions come with the feed's own trail (`t.trail`). The asset's kind
   * sets the landing-roll speed, as `toAircraftPosition` uses it. The landing evidence takes the project's
   * flight-plan strips besides the aerodrome list and the flight's own start-up position (decision 949).
   */
  function judged(r: AccountRuntime, t: TrackWithTrail): TrackWithTrail {
    if (t.position.providerFlying !== null) return t;
    const trail = [...(r.history.get(t.position.deviceId)?.trail ?? []), ...t.trail];
    if (trail.length === 0) return t;
    const kind = assetFor(t.position, assets)?.kind ?? t.position.kind ?? 'fixed_wing';
    const position = withStatus(t.position, trail, kind, strips);
    return position === t.position ? t : { ...t, position };
  }

  function rebuild(): void {
    const now = opts.now();
    const fire = opts.nafc?.() ?? null;
    const publicNow: TrackWithTrail[] = (traffic?.latest() ?? []).map((raw) => {
      // NAFC's fire aircraft get their registration, kind and call-sign row (decision 404)
      const position = withNafc(raw, fire);
      // a pinned aircraft keeps every fix, on the ground too (am:1001–1006); others only while moving
      const point = isPinned(position) ? observedPoint(position) : publicTrailPoint(position);
      return { position, trail: point ? [point] : [] };
    });
    const all = [...[...runtimes.values()].flatMap((r) => r.latest.map((t) => judged(r, t))), ...publicNow];
    // a pinned aircraft no feed lists now stays at its last observed position (up to 48 hours)
    const listed = new Set(publicNow.map((t) => observedCallsign(t.position)));
    const ghosts: TrackedPosition[] = [];
    for (const [cs, source] of pinned) {
      if (listed.has(cs)) continue;
      const last = (observedTrails.get(cs) ?? []).reduce<TrackPoint | null>(
        (b, p) => (!b || p.timeMs > b.timeMs ? p : b),
        null,
      );
      if (!last || now - last.timeMs > TRAIL_RETENTION_MS) continue;
      ghosts.push({
        ...last,
        source,
        account: '',
        deviceId: `${GHOST}${cs}`,
        label: cs,
        registration: null,
        icaoHex: null,
        kind: null,
        fleetGroup: '',
        sos: false,
        satellite: null,
        providerFlying: null,
        tripId: null,
      });
    }
    const positions = [
      ...all.map((t) => t.position),
      ...[...runtimes.values()].flatMap((r) => historyOnly(r)),
      ...ghosts,
    ];
    const entries = onePerAircraft(positions, assets, settings).map((m) => {
      const marked = isRfds(m.position, rfds);
      // the fire call-sign and role travel with the aircraft whichever feed is shown (bushfire.io's carry the role)
      const fire =
        m.members.find((p) => p.fire?.role)?.fire ?? m.position.fire ?? m.members.find((p) => p.fire)?.fire;
      const plain = toAircraftPosition(
        fire ? { ...m.position, fire } : m.position,
        m.asset,
        now,
        settings,
        marked,
      );
      // an Oz Runways or AvPlan aircraft can be pinned by its callsign (not one of the organisation's own)
      const cs =
        m.asset === null
          ? (m.members.map((p) => observedCallsign(p)).find((c): c is string => c !== null) ?? null)
          : null;
      const map: AircraftPosition = cs ? { ...plain, observed: cs, pinned: pinned.has(cs) } : plain;
      return { position: m.position, asset: m.asset, map, members: m.members };
    });
    // every position (and the device behind it) belongs to the aircraft of its group (issue #55)
    type Entry = (typeof entries)[number];
    const byMember = new Map<TrackedPosition, Entry>();
    const byDevice = new Map<string, Entry>();
    const plainId = (p: TrackedPosition): string => `${p.source}:${p.account}:${p.deviceId}`;
    for (const e of entries)
      for (const p of e.members) {
        byMember.set(p, e);
        byDevice.set(plainId(p), e);
      }
    const ownerOf = (p: TrackedPosition): Entry | undefined => byMember.get(p) ?? byDevice.get(plainId(p));
    // a trail kept under another id of the same aircraft (its device before it became an asset, another source,
    // another asset of the same registration) moves to the id shown
    for (const e of entries) {
      const ids = new Set<string>();
      for (const p of e.members) {
        ids.add(plainId(p));
        const own = assets.find(
          (a) =>
            !a.deleted_at &&
            a.provider === p.source &&
            a.account_label === p.account &&
            a.device_id === p.deviceId,
        );
        if (own) ids.add(`asset:${own.id}`);
      }
      for (const id of ids) {
        if (id === e.map.id || !trailStore.has(id)) continue;
        trailStore.set(e.map.id, mergeTrail(trailStore.get(e.map.id) ?? [], trailStore.get(id)!, now));
        trailStore.delete(id);
      }
    }
    // trails: every source's points for the aircraft, merged under its map id
    for (const t of all) {
      const owner = ownerOf(t.position);
      if (!owner || t.trail.length === 0) continue;
      trailStore.set(owner.map.id, mergeTrail(trailStore.get(owner.map.id) ?? [], t.trail, now));
    }
    // a pinned aircraft's observed trail (this laptop's, kept for the life of the project) joins its trail
    for (const e of entries) {
      const kept = e.map.pinned && e.map.observed ? observedTrails.get(e.map.observed) : undefined;
      if (kept && kept.length > 0)
        trailStore.set(e.map.id, mergeTrail(trailStore.get(e.map.id) ?? [], kept, now));
    }
    // history points, once each, under the aircraft they belong to
    for (const r of runtimes.values()) {
      for (const [dev, e] of r.history) {
        if (!e.fresh) continue;
        const p = r.latest.find((t) => t.position.deviceId === dev)?.position ?? e.position ?? null;
        const owner = p ? ownerOf(p) : undefined;
        if (!owner) continue;
        trailStore.set(owner.map.id, mergeTrail(trailStore.get(owner.map.id) ?? [], e.trail, now));
        e.fresh = false;
      }
    }
    const byId = new Map(entries.map((e) => [e.map.id, e]));
    for (const id of [...trailStore.keys()]) {
      const e = byId.get(id);
      // a pinned aircraft's trail is kept 48 hours, like the trackers'
      const pub: PublicTrafficId | null = e
        ? e.asset === null && isPublicTraffic(e.position.source) && !e.map.pinned
          ? e.position.source
          : null
        : publicSourceOfId(id);
      const cutoff = trailCutoff(pub, now);
      const kept = mergeTrail(trailStore.get(id)!, [], now).filter((p) => p.timeMs >= cutoff);
      if (kept.length === 0) trailStore.delete(id);
      else trailStore.set(id, kept);
    }
    // take-off, flight time and landing of the organisation's aircraft, from their trails (03-034)
    view = entries.map((e) => {
      const flight = e.map.fleet
        ? flightStatus(trailStore.get(e.map.id) ?? [], e.map.kind, now, strips)
        : null;
      return { position: e.position, asset: e.asset, map: flight ? { ...e.map, flight } : e.map };
    });
    // one lost-tracking, SOS or resumed alert per aircraft and episode, queued for the phone relay (03-037)
    const fleet = view.filter((v) => v.map.fleet);
    const fix = new Map(fleet.map((v) => [v.map.id, v.position.timeMs]));
    const raised = trackingAlerts(
      alertActive,
      fleet.map((v) => ({
        id: v.map.id,
        label: v.map.label,
        state: v.map.state ?? 'parked',
        ageMin: (now - v.position.timeMs) / 60_000,
      })),
    );
    alertActive = raised.active;
    alertQueue = [...alertQueue, ...alertsToQueue(raised.alerts, (id) => fix.get(id), now)].slice(
      -ALERT_QUEUE_LIMIT,
    );
    const shown = view.map((v) => v.map);
    for (const l of listeners) l(shown);
  }

  function fail(r: AccountRuntime, e: unknown): void {
    const name = accountName(r.account);
    const state: AccountState =
      e instanceof ProxyError
        ? e.code === 'not_set_up'
          ? 'not_set_up'
          : e.code === 'credential_refused'
            ? 'refused'
            : e.code === 'offline'
              ? 'offline'
              : 'error'
        : 'error';
    const kept = r.status.lastOkAt ? ` Showing the positions of ${clock(r.status.lastOkAt)}.` : '';
    r.status = {
      ...r.status,
      state,
      message: `${name}: ${words(e)}${state === 'offline' || state === 'error' ? kept : ''}`,
    };
    if (state === 'not_set_up' || state === 'refused') r.latest = [];
  }

  /** The account's status with its aircraft count and, when the history failed, why. */
  function statusOf(r: AccountRuntime): AccountStatus {
    if (r.status.state !== 'ok' || !r.status.lastOkAt) return r.status;
    const n = r.latest.length + historyOnly(r).length;
    const problem = r.historyProblem ? `. The earlier trail could not be read: ${r.historyProblem}` : '';
    return {
      ...r.status,
      aircraft: n,
      message: `${accountName(r.account)}: ${n} aircraft, updated ${clock(r.status.lastOkAt)}${problem}`,
    };
  }

  async function pollAccount(r: AccountRuntime, now: number): Promise<void> {
    const { provider, label } = r.account;
    const c = opts.client;
    let parsed: { value: TrackWithTrail[]; problem: string | null };
    let fetchedAt: string;
    if (provider === 'v2track') {
      if (now >= r.nextAssetsAt) {
        r.nextAssetsAt = now + V2TRACK_ASSETS_MS;
        try {
          r.assets = parseV2TrackAssets((await c.call(provider, label, 'assets')).body).value;
        } catch {
          /* assets only fill missing labels */
        }
      }
      if (now >= r.nextAlertsAt) {
        r.nextAlertsAt = now + V2TRACK_ALERTS_MS;
        try {
          r.sos = parseV2TrackAlerts((await c.call(provider, label, 'alerts')).body).value;
        } catch {
          /* keep the last SOS set until the next answer */
        }
      }
      const a = await c.call(provider, label, 'positions');
      const p = parseV2TrackPositions(a.body, label, r.sos, r.assets);
      parsed = {
        value: p.value.map((position) => {
          // a helicopter type in the device's trip answers, when its label and group did not say
          const kind = position.kind ?? kindFromTypeText(r.typeText.get(position.deviceId));
          const pos = kind === position.kind ? position : { ...position, kind };
          return { position: pos, trail: [pos] };
        }),
        problem: p.problem,
      };
      fetchedAt = a.fetchedAt;
    } else if (provider === 'spidertracks') {
      const a = await c.call(provider, label, 'latest');
      parsed = parseSpiderTracks(a.body, label);
      fetchedAt = a.fetchedAt;
    } else if (provider === 'websentinel') {
      const a = await c.call(provider, label, 'positions', {
        since_ms: Math.floor(now - POLL_MS.websentinel * 4),
      });
      parsed = parseWebSentinel(a.body, label);
      fetchedAt = a.fetchedAt;
    } else {
      // TracPlus refuses more than 24 hours (HTTP 400): a 48-hour depth asks 24
      const a = await c.call(provider, label, 'feed', { hours: tracPlusFeedHours(depth) });
      parsed = parseTracPlusKml(a.body, label, Date.parse(a.fetchedAt) || now);
      fetchedAt = a.fetchedAt;
    }
    if (parsed.problem) throw new ProxyError('provider_error', parsed.problem);
    r.latest = parsed.value;
    for (const t of parsed.value) {
      const seen = rfdsObservation(t.position, rfds);
      if (seen) {
        rfds.push({ id: `pending:${seen.callsign}`, ...seen, deleted_at: null });
        opts.onRfds?.(seen);
      }
    }
    const n = parsed.value.length;
    r.status = {
      provider,
      label,
      state: 'ok',
      message: `${accountName(r.account)}: ${n} aircraft, updated ${clock(fetchedAt)}`,
      lastOkAt: fetchedAt,
      aircraft: n,
    };
  }

  // ------------------------------------------------------------------------------------------ history

  /** Keep the points of one device inside the depth (and its end-of-flight position when it has one). */
  function addHistory(
    r: AccountRuntime,
    device: string,
    points: readonly TrackPoint[],
    cutoff: number,
    position: TrackedPosition | null = null,
  ): void {
    const e = r.history.get(device) ?? { trail: [], position: null, fresh: false };
    const inDepth = points.filter((p) => p.timeMs >= cutoff);
    e.trail = mergeTrail(e.trail, inDepth, opts.now());
    if (position && (!e.position || position.timeMs > e.position.timeMs)) e.position = position;
    e.fresh = true;
    r.history.set(device, e);
  }

  /** GetTripDataBulk in batches of 20; each trip's points under its device. */
  async function v2trackTrips(
    r: AccountRuntime,
    items: readonly { d: string; ti: string; mrp: string }[],
    cutoff: number,
  ): Promise<void> {
    for (let i = 0; i < items.length; i += V2TRACK_BATCH) {
      const batch = items.slice(i, i + V2TRACK_BATCH);
      const a = await opts.client.call('v2track', r.account.label, 'trips', { items: batch });
      const t = parseV2TrackTrips(a.body, cutoff);
      if (t.problem) throw new ProxyError('provider_error', t.problem);
      for (const trip of t.value.trips) {
        const device = trip.deviceId || batch.find((b) => b.ti === trip.tripId)?.d || '';
        if (!device) continue;
        if (trip.typeText) r.typeText.set(device, trip.typeText);
        addHistory(r, device, trip.points, cutoff);
      }
    }
  }

  /** V2Track: the trip list of the depth's days per device, then every trip's points (am:7235–7435). */
  async function v2trackHistory(r: AccountRuntime, now: number): Promise<void> {
    const label = r.account.label;
    const cutoff = now - depth * 3_600_000;
    const range = v2trackHistoryRange(now, depth);
    const devices = r.latest
      .map((t) => t.position)
      .filter((p) => !r.done.has(p.deviceId))
      .slice(0, HISTORY_PER_PASS.v2track);
    const problems: string[] = [];
    const items: { d: string; ti: string; mrp: string }[] = [];
    const answered: string[] = [];
    await Promise.all(
      devices.map(async (p) => {
        try {
          const a = await opts.client.call('v2track', label, 'history', {
            device_id: p.deviceId,
            ...range,
          });
          const h = parseV2TrackHistory(a.body, p.deviceId);
          if (h.problem) throw new ProxyError('provider_error', h.problem);
          items.push(...h.value);
          answered.push(p.deviceId);
        } catch (e) {
          problems.push(words(e));
        }
      }),
    );
    // the current trip may not be in the list yet (v2track_api.rs 425–433): add it when its fix is inside the depth
    for (const p of devices) {
      if (p.tripId && p.timeMs >= cutoff && !items.some((i) => i.ti === p.tripId))
        items.push({ d: p.deviceId, ti: p.tripId, mrp: '' });
    }
    try {
      await v2trackTrips(r, items, cutoff);
      for (const d of answered) r.done.add(d);
    } catch (e) {
      problems.push(words(e));
    }
    r.historyProblem = problems[0] ?? null;
    const left = r.latest.some((t) => !r.done.has(t.position.deviceId));
    r.nextHistoryAt = problems.length > 0 ? now + HISTORY_RETRY_MS : left ? now + V2TRACK_TRIPS_MS : Infinity;
    r.nextTripsAt = now + V2TRACK_TRIPS_MS;
  }

  /** V2Track: the current trip of each aircraft reporting now, every minute, in one call (am:6921–7226). */
  async function v2trackCurrentTrips(r: AccountRuntime, now: number): Promise<void> {
    r.nextTripsAt = now + V2TRACK_TRIPS_MS;
    const items = r.latest
      .map((t) => t.position)
      .filter((p) => p.tripId && now - p.timeMs < lostTrackingMinutes(settings, 'v2track') * 60_000)
      .map((p) => ({ d: p.deviceId, ti: p.tripId!, mrp: '' }));
    if (items.length === 0) return;
    await v2trackTrips(r, items, now - depth * 3_600_000);
  }

  /** Spider Tracks: completed flights inside the depth, each once (am:7867–7957). */
  async function spiderTracksHistory(r: AccountRuntime, now: number): Promise<void> {
    r.nextHistoryAt = now + SPIDERTRACKS_HISTORY_MS;
    const label = r.account.label;
    const cutoff = now - depth * 3_600_000;
    const a = await opts.client.call('spidertracks', label, 'tracks', { since_ms: cutoff });
    const live = new Set(r.latest.map((t) => t.position.tripId).filter((x): x is string => x !== null));
    const list = parseSpiderTracksList(a.body, cutoff, live);
    if (list.problem) throw new ProxyError('provider_error', list.problem);
    const todo = list.value.filter((t) => !r.done.has(t.trackId)).slice(0, HISTORY_PER_PASS.spidertracks);
    const problems: string[] = [];
    for (const t of todo) {
      // tried once for this depth: a flight that cannot be read is said, not asked for every 2 minutes
      r.done.add(t.trackId);
      try {
        const one = parseSpiderTrack(
          (await opts.client.call('spidertracks', label, 'track', { track_id: t.trackId })).body,
          label,
        );
        if (one.problem) throw new ProxyError('provider_error', one.problem);
        if (one.value)
          addHistory(r, one.value.position.deviceId, one.value.trail, cutoff, one.value.position);
      } catch (e) {
        problems.push(words(e));
      }
    }
    r.historyProblem =
      problems.length === 0
        ? null
        : `${problems.length} earlier ${problems.length === 1 ? 'flight' : 'flights'} (${problems[0]})`;
  }

  /** WebSentinel: each vehicle's position CSV since the cutoff, once per depth (commands.rs 1938–2010). */
  async function webSentinelHistory(r: AccountRuntime, now: number): Promise<void> {
    const label = r.account.label;
    const cutoff = Math.floor(now - depth * 3_600_000);
    const vehicles = r.latest
      .map((t) => t.position.deviceId)
      .filter((v) => !r.done.has(v))
      .slice(0, HISTORY_PER_PASS.websentinel);
    const problems: string[] = [];
    for (const v of vehicles) {
      try {
        const a = await opts.client.call('websentinel', label, 'history', {
          vehicle_id: v,
          since_ms: cutoff,
        });
        const csv = parseWebSentinelCsv(a.body);
        if (csv.problem) throw new ProxyError('provider_error', csv.problem);
        addHistory(r, v, csv.value, cutoff);
        r.done.add(v);
      } catch (e) {
        problems.push(words(e));
      }
    }
    r.historyProblem = problems[0] ?? null;
    const left = r.latest.some((t) => !r.done.has(t.position.deviceId));
    r.nextHistoryAt = problems.length > 0 ? now + HISTORY_RETRY_MS : left ? now + 60_000 : Infinity;
  }

  /** Start the history the account is due, in the background (the positions are drawn first). */
  function startHistory(r: AccountRuntime, now: number): void {
    const provider = r.account.provider;
    if (provider === 'tracplus' || r.status.state !== 'ok' || r.historyBusy) return;
    if (r.historyDepth !== depth) {
      r.historyDepth = depth;
      r.done = new Set();
      r.nextHistoryAt = 0;
    }
    let job: (() => Promise<void>) | null = null;
    if (now >= r.nextHistoryAt) {
      job =
        provider === 'v2track'
          ? () => v2trackHistory(r, now)
          : provider === 'spidertracks'
            ? () => spiderTracksHistory(r, now)
            : () => webSentinelHistory(r, now);
    } else if (provider === 'v2track' && now >= r.nextTripsAt) {
      job = () => v2trackCurrentTrips(r, now);
    }
    if (!job) return;
    r.historyBusy = true;
    const run = job()
      .catch((e: unknown) => {
        r.historyProblem = words(e);
        r.nextHistoryAt = Math.max(r.nextHistoryAt, now + HISTORY_RETRY_MS);
      })
      .finally(() => {
        r.historyBusy = false;
        pending.delete(run);
        rebuild();
      });
    pending.add(run);
  }

  return {
    get name(): string {
      const pub = traffic?.status() ?? [];
      if (runtimes.size === 0 && pub.length === 0) return 'No tracker set up';
      return [...[...runtimes.values()].map((r) => statusOf(r).message), ...pub.map((p) => p.message)].join(
        ' · ',
      );
    },
    get connected(): boolean {
      return (
        [...runtimes.values()].some((r) => r.status.state === 'ok') ||
        (traffic?.status() ?? []).some((p) => p.ok)
      );
    },
    subscribe(cb): Unsubscribe {
      listeners.add(cb);
      cb(view.map((v) => v.map));
      return () => listeners.delete(cb);
    },
    async poll(): Promise<void> {
      const now = opts.now();
      const due = [...runtimes.values()].filter((r) => now >= r.nextAt);
      // public traffic beside the trackers; an RFDS callsign seen there is remembered as from a tracker (Q46)
      const publicPoll = traffic
        ? traffic.poll().then((got) => {
            if (!got) return;
            for (const p of traffic.latest()) {
              const seen = rfdsObservation(p, rfds);
              if (seen) {
                rfds.push({ id: `pending:${seen.callsign}`, ...seen, deleted_at: null });
                opts.onRfds?.(seen);
              }
            }
          })
        : Promise.resolve();
      await Promise.all([
        publicPoll,
        ...due.map(async (r) => {
          r.nextAt = now + POLL_MS[r.account.provider];
          try {
            await pollAccount(r, now);
          } catch (e) {
            fail(r, e);
            // a refused or missing login is not retried every poll: wait 5 minutes or until it is replaced
            if (r.status.state === 'not_set_up' || r.status.state === 'refused') r.nextAt = now + 5 * 60_000;
          }
        }),
      ]);
      rebuild();
      for (const r of due) startHistory(r, now);
    },
    async settled(): Promise<void> {
      while (pending.size > 0) await Promise.all([...pending]);
    },
    status: () => [...runtimes.values()].map(statusOf),
    trails: () => trailStore,
    tracked: () => view,
    devices: () =>
      [...runtimes.values()].flatMap((r) => [...r.latest.map((t) => t.position), ...historyOnly(r)]),
    setAssets(a) {
      assets = a;
      rebuild();
    },
    setSettings(s) {
      settings = s;
      rebuild();
    },
    setHistoryHours(hours) {
      depth = historyDepth(hours);
    },
    setView(v) {
      if (!traffic) return;
      traffic.setView(v);
      rebuild();
    },
    setPublicTraffic(p) {
      if (!traffic) return;
      traffic.setPrefs(p);
      rebuild();
    },
    publicStatus: () =>
      (traffic?.status() ?? []).map((p) => ({
        source: p.source,
        message: p.message,
        ok: p.ok,
        credit: p.credit,
      })),
    setObserved(o) {
      pinned = new Map(o.pins.map((p) => [p.callsign, p.source]));
      observedTrails = o.trails;
      rebuild();
    },
    setStrips(s) {
      strips = s;
      rebuild();
    },
    worldTraffic: () => traffic?.everywhere() ?? [],
    takeAlerts() {
      const out = alertQueue;
      alertQueue = [];
      return out;
    },
  };
}
